'use client';

import { useRef, useState, type CSSProperties, type MouseEvent } from 'react';
import * as RPopover from '@radix-ui/react-popover';
import { cn } from '../../lib/cn';
import { FLOATING_SLIDE_ANIM } from '../../lib/floating-anim';
import { usePortalContainer } from '../../lib/portal-container';
import { SIcon, type SIconName } from '../SIcon';
import { STag } from '../STag';

export type SGhostButtonSize = 'xxs' | 'xs' | 'sm' | 'md' | 'lg';
export type SGhostButtonIntent = 'default' | 'danger' | 'action' | 'subAction' | 'inverse';

// 원본 sd-ghost-button.config: size/icon/radius (component.ghostButton 토큰과 동일)
const SIZE: Record<SGhostButtonSize, { icon: number; radius: 'sm' | 'md' }> = {
  xxs: { icon: 12, radius: 'sm' },
  xs: { icon: 16, radius: 'sm' },
  sm: { icon: 20, radius: 'sm' },
  md: { icon: 24, radius: 'md' },
  lg: { icon: 32, radius: 'md' },
};

/** mouseleave 후 닫힘까지의 유예 (STooltip HIDE_DELAY_MS와 동일) */
const TOOLTIP_HIDE_DELAY_MS = 100;

export interface SGhostButtonProps {
  /** 표시할 아이콘명 (필수) */
  icon: SIconName;
  /** 버튼 크기 */
  size?: SGhostButtonSize;
  /** 색상 의도 */
  intent?: SGhostButtonIntent;
  /** 스크린리더용 접근성 레이블 */
  ariaLabel?: string;
  /** 토글 버튼일 때 눌림 상태 (aria-pressed). 미지정 시 속성 자체를 내보내지 않는다. */
  ariaPressed?: boolean;
  /** disclosure 버튼일 때 펼침 상태 (aria-expanded) */
  ariaExpanded?: boolean;
  /** 호버 시 표시할 툴팁 텍스트 */
  tooltipText?: string;
  /** 비활성 */
  disabled?: boolean;
  /** 클릭 (sdClick) */
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  /** tooltip anchor wrapper 클래스. tooltipText가 있을 때 위치 조정용으로 사용합니다. */
  anchorClassName?: string;
  /** tooltip anchor wrapper 스타일. tooltipText가 있을 때 위치 조정용으로 사용합니다. */
  anchorStyle?: CSSProperties;
  className?: string;
  style?: CSSProperties;
}

/**
 * SGhostButton — sd-ghost-button 포팅. 아이콘 전용 투명 버튼.
 * hover 시 intent 색 오버레이(5%), focus-visible 링, 옵션 툴팁(회색 STag).
 */
export function SGhostButton({
  icon,
  size = 'sm',
  intent = 'default',
  ariaLabel = '',
  ariaPressed,
  ariaExpanded,
  tooltipText,
  disabled = false,
  onClick,
  anchorClassName,
  anchorStyle,
  className,
  style,
}: SGhostButtonProps) {
  const portalContainer = usePortalContainer();
  const [open, setOpen] = useState(false);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const cancelHide = () => {
    if (hideTimer.current) {
      clearTimeout(hideTimer.current);
      hideTimer.current = undefined;
    }
  };
  const openNow = () => {
    cancelHide();
    setOpen(true);
  };
  // mouseleave 후 100ms 유예 뒤 닫힘 (본문 hover 시 취소됨)
  const startHide = () => {
    cancelHide();
    hideTimer.current = setTimeout(() => setOpen(false), TOOLTIP_HIDE_DELAY_MS);
  };
  const s = SIZE[size];
  const contentColor = disabled
    ? intent === 'inverse'
      ? 'var(--cmp-ghostButton-inverse-disabled)'
      : 'var(--cmp-ghostButton-icon-disabled)'
    : `var(--cmp-ghostButton-${intent}-content)`;
  const text = (tooltipText ?? '').trim();
  const hasTooltip = text !== '';

  const btn = (
    <button
      type="button"
      disabled={disabled}
      aria-disabled={disabled ? 'true' : undefined}
      aria-label={ariaLabel.trim() || undefined}
      aria-pressed={ariaPressed}
      aria-expanded={ariaExpanded}
      tabIndex={disabled ? -1 : undefined}
      onClick={e => {
        if (disabled) {
          e.preventDefault();
          return;
        }
        onClick?.(e);
      }}
      className={cn(
        'group/ghost-button relative box-border inline-flex items-center justify-center border-none bg-transparent p-0 transition-shadow',
        disabled ? 'cursor-not-allowed' : 'cursor-pointer',
        'focus-visible:outline-none focus-visible:shadow-[0_0_0_2px_var(--cmp-ghostButton-action-hover-bg)]',
        className,
      )}
      style={{
        width: `var(--cmp-ghostButton-${size}-size)`,
        height: `var(--cmp-ghostButton-${size}-size)`,
        borderRadius: `var(--cmp-ghostButton-radius-${s.radius})`,
        ...style,
      }}
    >
      {/* hover 오버레이 (::before 대응) — 비활성 아닐 때만 */}
      {!disabled && (
        <span
          aria-hidden
          className="pointer-events-none absolute inset-0 rounded-[inherit] opacity-0 transition-opacity duration-200 group-hover/ghost-button:opacity-5"
          style={{ background: `var(--cmp-ghostButton-${intent}-hover-bg)` }}
        />
      )}
      <SIcon name={icon} size={s.icon} color={contentColor} />
    </button>
  );

  if (!hasTooltip) return btn;

  return (
    <RPopover.Root open={open} onOpenChange={setOpen}>
      {/* hover: 즉시 열림 + mouseleave 100ms 유예 (STooltip 인터랙션 미러). 클릭 토글 방지 위해 Anchor 사용 */}
      <RPopover.Anchor asChild>
        <span
          className={cn('inline-flex', anchorClassName)}
          style={anchorStyle}
          onMouseEnter={openNow}
          onMouseLeave={startHide}
          onFocus={openNow}
          onBlur={startHide}
        >
          {btn}
        </span>
      </RPopover.Anchor>
      <RPopover.Portal container={portalContainer}>
        <RPopover.Content
          side="top"
          sideOffset={4}
          className={cn('z-50', FLOATING_SLIDE_ANIM)}
          aria-hidden
          // 포커스 가로채지 않음 (툴팁은 포커스 이동 없음)
          onOpenAutoFocus={e => e.preventDefault()}
          // hover 브릿지: 태그 위로 마우스 이동 시 닫힘 취소, 벗어나면 유예 후 닫힘
          onMouseEnter={cancelHide}
          onMouseLeave={startHide}
        >
          <STag shape="square" size="xs" color="grey" label={text} />
        </RPopover.Content>
      </RPopover.Portal>
    </RPopover.Root>
  );
}
